import { BarChart07, Edit03, LineChartUp01, Table as TableIcon, Trash03 } from "@untitledui/icons";
import type React from "react";
import { useMemo } from "react";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type {
  UnboxTableColumn,
  UnboxTableEmptyState,
  UnboxTableProps,
} from "@/components/application/table/unbox-table.types";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import type { ReportRow } from "./Reports";

const NO_RESULTS_EMPTY_STATE: UnboxTableEmptyState = {
  icon: LineChartUp01,
  title: "Your search didn't return any results",
  description: (
    <>
      Try adjusting your filters or search terms
      <br />
      to see more results.
    </>
  ),
};

export interface ReportsTableProps
  extends Omit<
    UnboxTableProps<ReportRow>,
    "columns" | "rowId" | "emptyState" | "filteredEmptyState" | "size" | "rounded" | "className" | "aria-label"
  > {
  onClearFilters: () => void;
  onDeleteReport: (reportId: string, reportName: string, layout: "chart" | "table" | "bar") => void;
  onEditReport: (reportId: string) => void;
}

export function ReportsTable({
  rows,
  onClickRow,
  sorting,
  pagination,
  hasActiveFilters,
  onClearFilters,
  onDeleteReport,
  onEditReport,
}: ReportsTableProps) {
  const columns = useMemo<UnboxTableColumn<ReportRow>[]>(
    () => [
      {
        id: "name",
        label: "Name",
        sortable: true,
        minWidth: "280px",
        render: (_value, row) => (
          <span className="text-sm font-medium text-primary whitespace-nowrap" title={row.name || ""}>
            {row.name}
          </span>
        ),
      },
      {
        id: "reportLayout",
        label: "Layout",
        sortable: true,
        minWidth: "109px",
        render: (_value, row) => (
          <Badge type="modern" color="gray" className="flex flex-row items-center gap-2 whitespace-nowrap">
            {row.layout === "chart" ? (
              <LineChartUp01 className="w-3 h-3" />
            ) : row.layout === "table" ? (
              <TableIcon className="w-3 h-3" />
            ) : (
              <BarChart07 className="w-3 h-3" />
            )}
            {row.layout === "chart" ? "Chart" : row.layout === "table" ? "Table" : "Bar"}
          </Badge>
        ),
      },
      {
        id: "reportMetrics",
        label: "Metrics",
        sortable: true,
        minWidth: "109px",
        render: (_value, row) => (
          <span className="text-sm font-medium text-secondary whitespace-nowrap" title={row.metrics || ""}>
            {row.metrics}
          </span>
        ),
      },
      {
        id: "createdAt",
        label: "Created",
        sortable: true,
        minWidth: "109px",
        render: (_value, row) =>
          row.createdAt
            ? row.createdAt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" })
            : "N/A",
      },
      {
        id: "updatedAt",
        label: "Last Edited",
        sortable: true,
        minWidth: "109px",
        render: (_value, row) => {
          if (!row.updatedAt) return "N/A";
          const dateStr = row.updatedAt.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          });
          const timeStr = row.updatedAt.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
          return `${dateStr} @${timeStr}`;
        },
      },
      {
        id: "actions",
        label: "",
        minWidth: "109px",
        render: (_value, row) => (
          <div className="flex items-center gap-1">
            <Button
              color="tertiary"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDeleteReport(row.id, row.name, row.layout);
              }}
            >
              <Trash03 className="w-4 h-4 text-gray-400 hover:text-red-600" />
            </Button>
            <Button
              color="tertiary"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onEditReport(row.id);
              }}
            >
              <Edit03 className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </Button>
          </div>
        ),
      },
    ],
    [onDeleteReport, onEditReport],
  );

  return (
    <UnboxTable<ReportRow>
      aria-label="Reports"
      className="max-h-[60vh]"
      columns={columns}
      rows={rows}
      rowId="id"
      onClickRow={onClickRow}
      sorting={sorting}
      pagination={pagination}
      hasActiveFilters={hasActiveFilters}
      emptyState={NO_RESULTS_EMPTY_STATE}
      filteredEmptyState={{ ...NO_RESULTS_EMPTY_STATE, onClearFilters }}
    />
  );
}
